<template>
    <div
        ref="wrapper"
        :class="$style.wrapper"
        @mouseenter="onWrapperEnter"
        @mouseleave="onWrapperLeave"
    >
        <IconButton
            color="carbon-purple-400"
            icon="circles4"
            interfaceTarget="open-more-menu"
            :class="isOpen && $style.trigger_active_override"
            :aria-label="$tr('tiles.more')"
            :aria-expanded="isOpen"
            @click="onTriggerClick"
        />

        <Transition
            :enterActiveClass="$style.menu_enter_active"
            :enterFromClass="$style.menu_hidden"
            :leaveActiveClass="$style.menu_leave_active"
            :leaveToClass="$style.menu_hidden"
        >
            <div
                v-if="isOpen"
                :class="$style.menu"
            >
                <Tooltip
                    v-if="isAuthorized"
                    ref="logged-in-tooltip"
                    :content="$tr('gameContainer.logged_in_tooltip')"
                    :showOnMobile="true"
                >
                    <Button
                        :class="$style.item_override"
                        color="carbon-purple-400"
                        leftIcon="happy-cloud"
                        interfaceTarget="more-menu-login-button"
                        :label="$tr('gameContainer.save')"
                        @click="onLoggedInClick"
                    />
                </Tooltip>

                <Button
                    v-for="item in items"
                    :key="item.interfaceTarget"
                    :class="[$style.item_override, item.variant === 'red' && $style.save_red_override]"
                    color="carbon-purple-400"
                    :leftIcon="item.icon"
                    :interfaceTarget="item.interfaceTarget"
                    :label="item.label"
                    @click="item.onClick"
                />
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { onClickOutside, onKeyStroke } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, toRef, useTemplateRef } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import IconButton from '@/components/Buttons/IconButton.vue'
import { type IconNameType } from '@/components/Icon'
import Tooltip from '@/components/Tooltip/Tooltip.vue'
import { useClipboard } from '@/composables/use-clipboard'
import { CAMPAIGN_IDS, useGoToApp } from '@/composables/use-go-to-app'
import { useTr } from '@/composables/use-tr'
import { useUserInfoLoaded } from '@/composables/use-user-state'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import { useUserStore } from '@/store/user-store/user-store'
import type { APIGameType } from '@/types'

import { usePartnerUrls } from '../../../composables/use-partner-urls'

const props = defineProps<{
    game: APIGameType
}>()

const wrapper = useTemplateRef('wrapper')
const loggedInTooltip = useTemplateRef('logged-in-tooltip')
const isOpen = ref(false)
const isOpenedByHover = ref(false)

const appStore = useAppStore()
const layoutStore = useLayoutStore()
const userStore = useUserStore()
const { isAuthorized } = storeToRefs(userStore)
const userInfoLoaded = useUserInfoLoaded()
const tr = useTr()

const { goToApp } = useGoToApp(CAMPAIGN_IDS.UNDER_GAME_ICON)
const { exportUrl } = usePartnerUrls(toRef(() => props.game.hru))
const { copied, copyToClipboard } = useClipboard({ onSuccess: () => close(), onError: () => close() })

type MenuItem = {
    interfaceTarget: string
    label: string
    icon?: IconNameType
    variant?: 'red'
    onClick: () => void
}

const items = computed<MenuItem[]>(() => {
    const showEmbed = Boolean(props.game.is_in_export_catalog) && props.game.source === 'playgama'
    const showPlayOnApp = !appStore.isAppEnv
    const showSaveLogin = !isAuthorized.value && userInfoLoaded.value
    const showSaveLoading = !isAuthorized.value && !userInfoLoaded.value

    const all: Array<MenuItem | false> = [
        showSaveLogin && {
            interfaceTarget: 'more-menu-login-button',
            label: tr('gameContainer.save'),
            icon: 'sad-cloud',
            variant: 'red',
            onClick: onLogin,
        },
        showSaveLoading && {
            interfaceTarget: 'more-menu-login-button',
            label: tr('gameContainer.save'),
            onClick: close,
        },
        showEmbed && {
            interfaceTarget: 'more-menu-embed',
            label: copied.value ? tr('gameContainer.share_popup_tooltip_copied') : tr('gameContainer.embed'),
            icon: 'tag',
            onClick: onEmbed,
        },
        {
            interfaceTarget: 'more-menu-report',
            label: tr('gameContainer.report'),
            icon: 'hand',
            onClick: onReport,
        },
        showPlayOnApp && {
            interfaceTarget: 'more-menu-play-on-app',
            label: tr('gameDetails.androidButton'),
            icon: 'qr-code',
            onClick: onPlayOnApp,
        },
    ]

    return all.filter((item): item is MenuItem => Boolean(item))
})

function onTriggerClick() {
    if (isOpen.value && isOpenedByHover.value) {
        isOpenedByHover.value = false
        return
    }
    isOpen.value = !isOpen.value
    isOpenedByHover.value = false
}

function close() {
    isOpen.value = false
    isOpenedByHover.value = false
}

function onWrapperEnter() {
    if (appStore.platform !== 'desktop') {
        return
    }
    isOpenedByHover.value = !isOpen.value
    isOpen.value = true
}

function onWrapperLeave() {
    if (appStore.platform !== 'desktop') {
        return
    }
    close()
}

function onLogin() {
    layoutStore.openSignInModal('game-desktop-footer-more-menu-login-button')
    close()
}

function onLoggedInClick() {
    if (loggedInTooltip.value?.isShown) {
        close()
        return
    }
    loggedInTooltip.value?.showTooltip()
    setTimeout(() => loggedInTooltip.value?.hideTooltip(), 2000)
}

function onEmbed() {
    copyToClipboard(exportUrl.value)
}

function onReport() {
    layoutStore.setIsReportBugModalOpen(true)
    close()
}

function onPlayOnApp() {
    goToApp()
    close()
}

onClickOutside(wrapper, () => close())

onKeyStroke('Escape', () => close())
</script>

<style module>
.wrapper {
    position: relative;
    display: flex;
}

.trigger_active_override {
    --gradient: linear-gradient(0deg, var(--white-08), var(--white-08));
}

.trigger_active_override:before {
    background-image: var(--gradient);
}

.menu {
    position: absolute;
    right: 0;
    bottom: calc(100% + 4px);
    z-index: 5;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
}

.menu::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    height: 4px;
}

.item_override {
    padding: 13px 12px;
}

.save_red_override {
    --background-color: var(--rose-100);
    color: var(--rose-400);
}

.menu_enter_active,
.menu_leave_active {
    transition:
        opacity 0.15s ease,
        transform 0.15s ease;
}

.menu_hidden {
    opacity: 0;
    transform: translateY(6px);
}
</style>
